import React, { useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import styles from 'src/app/Receipt Rules/OrgSettingsReceiptVerificationBox/OrgSettingsReceiptVerificationBox.style'
import BetaTag from 'src/components/BetaTag/BetaTag'
import Box from 'src/components/Box/Box'
import ErrorList from 'src/components/ErrorList/ErrorList'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationsListModel } from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import { usePutOrgReceiptVerificationEnabled } from 'src/queries/organizations/receiptVerification.queries'

interface OrgSettingsReceiptVerificationBoxProps {
  user?: UserListModel
  organization: OrganizationsListModel
}

const OrgSettingsReceiptVerificationBox: React.FC<
  OrgSettingsReceiptVerificationBoxProps
> = (props): React.ReactElement => {
  const { user, organization } = props

  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const { mutateAsync: putReceiptVerificationEnabled, isPending } =
    usePutOrgReceiptVerificationEnabled()
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  )

  const handleToggle = async (enabled: boolean): Promise<boolean> => {
    setErrorMessage(undefined)
    try {
      await putReceiptVerificationEnabled({
        id: organization.id,
        body: { enabled },
      })
      analytics?.track('receipt verification setting', {
        action: 'updated',
        enabled,
      })
      return true
    } catch {
      setErrorMessage(
        translate("Couldn't update Receipt Verification. Please try again."),
      )
      return false
    }
  }

  return (
    <Box
      css={styles}
      className={'OrgSettingsReceiptVerificationBox flex-column'}
      noShadow
      outlineBorder
    >
      <div className={'heading-row'}>
        <span className={'heading-text'}>
          {translate('Receipt Verification')}
        </span>
        <BetaTag isBetaTagHeader />
      </div>
      <span className={'body-text'}>
        {translate(
          'Enable receipt image verification to automatically check that uploaded receipts match the transaction details.',
        )}
      </span>
      <span className={'switch-label'}>
        {translate('Receipt Image Verification')}
      </span>
      <div className={'switch-container'}>
        <OnOffSwitch
          defaultOnOffState={organization.receiptVerificationEnabled}
          onChange={handleToggle}
          disabled={!user?.hasOrgAdminPermissions || isPending}
          testId={'receiptVerificationSwitch'}
        />
      </div>
      {errorMessage !== undefined && (
        <ErrorList errors={[errorMessage]} className={'error-message'} />
      )}
    </Box>
  )
}

export default OrgSettingsReceiptVerificationBox
